// ... imports
import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Modal } from 'antd'; // Modal kept for fallback or removed if using Drawer externally
import type { IndustryChainData, IndustryNode, IndustryEdge } from '../types';

interface IndustryChainMapProps {
  data: IndustryChainData;
  width?: number;
  height?: number;
  onNodeClick?: (node: IndustryNode) => void;
}

// 节点类型颜色映射 (深色主题优化)
const NODE_COLORS: Record<string, string> = {
  mining: '#D4A574',      // 浅棕色 - 矿业开采
  smelting: '#FF8C61',    // 亮橙色 - 冶炼加工
  application: '#5FD4C4', // 亮青色 - 应用领域
};

// 节点类型名称映射
const NODE_TYPE_NAMES: Record<string, string> = {
  mining: '矿业开采',
  smelting: '冶炼加工',
  application: '应用领域',
};

export default function IndustryChainMap({
  data,
  width = 1000,
  height = 600,
  onNodeClick,
}: IndustryChainMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  // State for path highlighting
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [connectedNodes, setConnectedNodes] = useState<Set<string>>(new Set());
  const [connectedLinks, setConnectedLinks] = useState<Set<string>>(new Set());

  // Helper to find all connected nodes (upstream and downstream)
  const findConnectedPath = (nodeId: string, allNodes: IndustryNode[], allEdges: IndustryEdge[]) => {
    const connectedN = new Set<string>([nodeId]);
    const connectedL = new Set<string>();

    // Traverse directions
    const traverse = (currentId: string, direction: 'upstream' | 'downstream') => {
      const relevantEdges = allEdges.filter(e =>
        direction === 'upstream' ? e.target === currentId : e.source === currentId
      );

      relevantEdges.forEach(e => {
        const nextNodeId = direction === 'upstream' ? e.source : e.target;
        if (!connectedN.has(nextNodeId)) {
          connectedN.add(nextNodeId);
          connectedL.add(`${e.source}-${e.target}`);
          traverse(nextNodeId, direction);
        } else {
          connectedL.add(`${e.source}-${e.target}`);
        }
      });
    };

    traverse(nodeId, 'upstream');
    traverse(nodeId, 'downstream');

    return { nodes: connectedN, links: connectedL };
  };

  useEffect(() => {
    if (!svgRef.current || !data || !data.nodes || data.nodes.length === 0) {
      return;
    }

    // 清空之前的内容
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current);

    // Deep clone data for D3 to avoid mutating props (React Query/Strict Mode issue)
    const nodes: IndustryNode[] = JSON.parse(JSON.stringify(data.nodes));
    const edges: IndustryEdge[] = JSON.parse(JSON.stringify(data.edges));

    // --- 1. Define SVG Filters and Gradients (Glow & Cyberpunk style) ---
    const defs = svg.append('defs');

    // Glow Filter
    const filter = defs.append('filter')
      .attr('id', 'glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');

    filter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Link Gradient
    const gradient = defs.append('linearGradient')
      .attr('id', 'link-gradient')
      .attr('gradientUnits', 'userSpaceOnUse');

    // --- 2. Setup Container and Zoom ---
    const container = svg.append('g');

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
      });

    svg.call(zoom);

    // --- 3. Force Simulation ---
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(edges)
        .id((d: any) => d.id)
        .distance(150))
      .force('charge', d3.forceManyBody().strength(-800))
      .force('collide', d3.forceCollide().radius(60))
      .force('y', d3.forceY(height / 2).strength(0.1))
      .force('x', d3.forceX((d: any) => {
        if (d.type === 'mining') return width * 0.15;
        if (d.type === 'smelting') return width * 0.5;
        if (d.type === 'application') return width * 0.85;
        return width / 2;
      }).strength(1));

    // --- 4. Render Edges (Background Lines) ---
    const linkGroup = container.append('g').attr('class', 'links');

    const link = linkGroup
      .selectAll('path')
      .data(edges)
      .join('path')
      .attr('fill', 'none')
      .attr('stroke', '#e0e0e0') // Light grey for base links
      .attr('stroke-width', 1)
      .attr('opacity', 0.8);

    // --- 5. Render Flow Particles ---
    const particleGroup = container.append('g').attr('class', 'particles');

    // Create particles for each edge
    const particles = particleGroup.selectAll('circle')
      .data(edges)
      .enter().append('circle')
      .attr('r', 3)
      .attr('fill', '#1890ff') // Blue particles
      .attr('opacity', 0); // Initially hidden, shown by animation

    // --- 6. Render Nodes ---
    const nodeGroup = container.append('g').attr('class', 'nodes');

    const node = nodeGroup
      .selectAll('g')
      .data(nodes)
      .enter().append('g')
      .attr('cursor', 'pointer')
      .call(d3.drag<any, IndustryNode>()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended) as any);

    // Node Circle
    node.append('circle')
      .attr('r', (d: any) => d.market_size ? Math.log(d.market_size) * 3 + 15 : 20)
      .attr('fill', (d: any) => NODE_COLORS[d.type] || '#fff')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))') // Subtle shadow
      .style('transition', 'all 0.3s ease');

    // Node Label
    node.append('text')
      .text((d: any) => d.name)
      .attr('x', 0)
      .attr('y', 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('fill', '#333')
      .attr('font-weight', '500')
      .style('pointer-events', 'none')
      .style('text-shadow', '0 1px 2px rgba(255,255,255,0.8)');

    // --- 7. Interaction Handlers ---
    node.on('mouseover', (event, d) => {
      const { nodes, links } = findConnectedPath(d.id, data.nodes, data.edges);
      setHoveredNode(d.id);
      setConnectedNodes(nodes);
      setConnectedLinks(links);
    })
      .on('mouseout', () => {
        setHoveredNode(null);
        setConnectedNodes(new Set());
        setConnectedLinks(new Set());
      })
      .on('click', (event, d) => {
        event.stopPropagation();
        if (onNodeClick) onNodeClick(d);
      });

    // --- 8. Simulation Tick & Animation Loop ---
    simulation.on('tick', () => {
      link.attr('d', (d: any) => {
        return `M${d.source.x},${d.source.y}C${(d.source.x + d.target.x) / 2},${d.source.y} ${(d.source.x + d.target.x) / 2},${d.target.y} ${d.target.x},${d.target.y}`;
      });
      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
      const time = Date.now() / 1500;
      particles.each(function (d: any) {
        const path = link.filter((l: any) => l === d).node();
        if (path && (path as SVGPathElement).getTotalLength) {
          const len = (path as SVGPathElement).getTotalLength();
          const t = (time % 1);
          const p = (path as SVGPathElement).getPointAtLength(t * len);
          d3.select(this)
            .attr('cx', p.x)
            .attr('cy', p.y)
            .attr('opacity', 0); // Hide default, rely on hover? Or show always? Let's show on hover mainly or keep hidden.
          // Actually let's keep logic simple: styling handles visibility
        }
      });
    });

    // Drag functions
    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }
    function dragged(event: any, d: any) {
      d.fx = event.x;
      d.fy = event.y;
    }
    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    return () => {
      simulation.stop();
    };
  }, [data, width, height, onNodeClick]);

  // Separate Effect for highlight
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const isHovering = hoveredNode !== null;

    // Update Nodes
    svg.selectAll('.nodes g')
      .transition().duration(200)
      .style('opacity', (d: any) => {
        if (!isHovering) return 1;
        return connectedNodes.has(d.id) ? 1 : 0.2;
      });

    // Update Links
    svg.selectAll('.links path')
      .transition().duration(200)
      .attr('stroke', (d: any) => {
        if (isHovering && connectedLinks.has(`${d.source.id}-${d.target.id}`)) return '#1890ff';
        return '#e0e0e0';
      })
      .attr('stroke-width', (d: any) => {
        if (isHovering && connectedLinks.has(`${d.source.id}-${d.target.id}`)) return 2;
        return 1;
      })
      .attr('opacity', (d: any) => {
        if (!isHovering) return 0.8;
        return connectedLinks.has(`${d.source.id}-${d.target.id}`) ? 1 : 0.1;
      });

    // Update Particles
    svg.selectAll('.particles circle')
      .attr('opacity', (d: any) => {
        if (isHovering && connectedLinks.has(`${d.source.id}-${d.target.id}`)) return 1;
        return 0;
      });

  }, [hoveredNode, connectedNodes, connectedLinks]);

  return (
    <div className="svg-container" style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px', background: '#f5f7fa' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />
      {/* Layer Labels */}
      <div style={{ position: 'absolute', top: 10, left: '15%', color: '#D4A574', fontWeight: 'bold' }}>上游 (矿产资源)</div>
      <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', color: '#FF8C61', fontWeight: 'bold' }}>中游 (冶炼加工)</div>
      <div style={{ position: 'absolute', top: 10, right: '15%', color: '#5FD4C4', fontWeight: 'bold' }}>下游 (应用终端)</div>
    </div>
  );
}
